import type { ElementType, ComponentPropsWithoutRef } from 'react';
import type { SxProps } from '@mui/material';
import { forwardRef } from 'react';
import MuiButton from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, styled } from '@mui/material/styles';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonOwnProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  sx?: SxProps;
  glow?: boolean;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  component?: ElementType;
  to?: string;
  href?: string;
}

type ButtonProps<C extends ElementType = 'button'> = ButtonOwnProps &
  Omit<ComponentPropsWithoutRef<C>, keyof ButtonOwnProps>;

type MuiColor = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'inherit';
type MuiVariant = 'contained' | 'outlined' | 'text';

const variantMap = {
  primary: { muiVariant: 'contained' as const, color: 'primary' as const },
  secondary: { muiVariant: 'contained' as const, color: 'secondary' as const },
  outline: { muiVariant: 'outlined' as const, color: 'primary' as const },
  danger: { muiVariant: 'contained' as const, color: 'error' as const },
  ghost: { muiVariant: 'text' as const, color: 'primary' as const },
} as const satisfies Record<ButtonVariant, { muiVariant: MuiVariant; color: MuiColor }>;

const sizeMap = {
  sm: 'small' as const,
  md: 'medium' as const,
  lg: 'large' as const,
};

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'glow',
})<{ glow?: boolean; component?: React.ElementType }>(({ theme, glow, color }) => ({
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.2s ease',
  '&:hover:not(:disabled)': {
    transform: 'translateY(-2px)',
  },
  '&:active:not(:disabled)': {
    transform: 'translateY(0)',
  },
  ...(glow && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: `linear-gradient(
        90deg,
        transparent,
        ${alpha(color === 'secondary' ? theme.palette.secondary.main : theme.palette.primary.main, 0.2)},
        transparent
      )`,
      animation: 'shimmer 2s infinite',
    },
    '@keyframes shimmer': {
      '100%': {
        left: '100%',
      },
    },
  }),
}));

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      glow = false,
      children,
      disabled,
      component,
      ...props
    },
    ref
  ) => {
    const { muiVariant, color } = variantMap[variant];
    const muiSize = sizeMap[size];

    return (
      <StyledButton
        ref={ref}
        variant={muiVariant}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        color={color as any}
        size={muiSize}
        disabled={disabled || isLoading}
        glow={glow && !isLoading && !disabled}
        component={component}
        {...props}
      >
        {isLoading ? (
          <>
            <CircularProgress
              size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20}
              color="inherit"
              sx={{ mr: 1 }}
            />
            Loading...
          </>
        ) : (
          children
        )}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';
